import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Draw as DrawIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import SignaturePad from '../HR/Forms/SignaturePad';

const MAX_UPLOAD_BYTES = 500 * 1024;

const SalesSignatureSection = ({ userId, canSetDefaultSigner }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [salesPerson, setSalesPerson] = useState(null);
  const [positions, setPositions] = useState([]);
  const [positionId, setPositionId] = useState('');
  const [isDefaultSigner, setIsDefaultSigner] = useState(false);
  const [newSignature, setNewSignature] = useState(null);
  const [padKey, setPadKey] = useState(0);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await makeApiCall(`api/users/${userId}/sales-signature`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setSalesPerson(data.sales_person);
      setPositions(data.positions || []);
      setPositionId(data.sales_person.position_id || '');
      setIsDefaultSigner(!!data.sales_person.is_default_signer);
    } catch (err) {
      setError(t('salesSignature.loadError'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (changes, successMessage) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await makeApiCall(`api/users/${userId}/sales-signature`, {
        method: 'PUT',
        body: JSON.stringify(changes),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setSalesPerson(data.sales_person);
      setNewSignature(null);
      setPadKey((k) => k + 1);
      setSuccess(successMessage);
    } catch (err) {
      setError(err.message || t('salesSignature.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const changes = { position_id: positionId || null };
    if (newSignature) changes.sign_image_data = newSignature;
    if (canSetDefaultSigner) changes.is_default_signer = isDefaultSigner;
    save(changes, t('salesSignature.saved'));
  };

  const handleRemove = () => {
    setConfirmRemoveOpen(false);
    save({ sign_image_data: null }, t('salesSignature.removed'));
  };

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > MAX_UPLOAD_BYTES) {
      setError(t('salesSignature.invalidFile'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setError('');
      setNewSignature(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const previewImage = newSignature || salesPerson?.sign_image_data;

  return (
    <Accordion defaultExpanded sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="sales-signature-content" id="sales-signature-header">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DrawIcon color="primary" />
          <Typography variant="h6">{t('salesSignature.title')}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails id="sales-signature-content">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} aria-label={t('salesSignature.loading')} />
          </Box>
        ) : !salesPerson ? (
          error && <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('salesSignature.intro')}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {newSignature ? t('salesSignature.newPreview') : t('salesSignature.current')}
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: '#fff',
                  p: 1,
                  minHeight: 90,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  maxWidth: 420,
                }}
              >
                {previewImage ? (
                  <img
                    src={previewImage}
                    alt={t('salesSignature.previewAlt', { name: salesPerson.display_name || '' })}
                    style={{ maxWidth: '100%', maxHeight: 120 }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('salesSignature.none')}
                  </Typography>
                )}
              </Box>
            </Box>

            <SignaturePad
              key={padKey}
              label={t('salesSignature.drawNew')}
              language={i18n.language?.startsWith('es') ? 'es' : 'en'}
              onSignature={setNewSignature}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadIcon />}
                aria-label={t('salesSignature.upload')}
              >
                {t('salesSignature.upload')}
                <input type="file" hidden accept="image/png,image/jpeg" onChange={handleUpload} />
              </Button>
              {salesPerson.sign_image_data && (
                <Button
                  color="error"
                  variant="text"
                  onClick={() => setConfirmRemoveOpen(true)}
                  disabled={saving}
                  aria-label={t('salesSignature.remove')}
                >
                  {t('salesSignature.remove')}
                </Button>
              )}
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth sx={{ minWidth: 220 }}>
                  <InputLabel id="sales-signature-position-label">{t('salesSignature.position')}</InputLabel>
                  <Select
                    labelId="sales-signature-position-label"
                    value={positionId}
                    label={t('salesSignature.position')}
                    onChange={(e) => setPositionId(e.target.value)}
                    inputProps={{ 'aria-label': t('salesSignature.position') }}
                  >
                    <MenuItem value=""><em>{t('salesSignature.noPosition')}</em></MenuItem>
                    {positions.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.position_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {canSetDefaultSigner && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={isDefaultSigner}
                        onChange={(e) => setIsDefaultSigner(e.target.checked)}
                        inputProps={{ 'aria-label': t('salesSignature.defaultSigner') }}
                      />
                    )}
                    label={t('salesSignature.defaultSigner')}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
                    {t('salesSignature.defaultSignerHelp')}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ minWidth: 160 }}
                aria-label={t('salesSignature.save')}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : t('salesSignature.save')}
              </Button>
            </Box>
          </>
        )}
      </AccordionDetails>

      <Dialog
        open={confirmRemoveOpen}
        onClose={() => setConfirmRemoveOpen(false)}
        aria-labelledby="remove-signature-title"
        aria-describedby="remove-signature-description"
      >
        <DialogTitle id="remove-signature-title">{t('salesSignature.removeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-signature-description">
            {t('salesSignature.removeConfirmBody')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveOpen(false)}>{t('salesSignature.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleRemove}>
            {t('salesSignature.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
};

export default SalesSignatureSection;
